"use client";

import React, { useRef, useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faChevronLeft,
    faChevronRight,
    faCircleX,
    faCircleCheck,
} from "@fortawesome/pro-regular-svg-icons";
import cn from "classnames";
import { useConfig } from "@peracto/peracto-config";

export function ProductComparisonTable() {
    const scrollContainerRef = useRef(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);
    const [showScrollButtons, setShowScrollButtons] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [comparisonProducts, setComparisonProducts] = useState([]);

    const config = useConfig();
    const API_URL = config.get("api");

    const loadTableData = async () => {
        setIsLoading(true);
        try {
            const response = await fetch(
                `${API_URL}/storefront/product-by-type/food_waste_disposal`
            );

            if (response.ok) {
                const json = await response.json();
                const products = json?.["hydra:member"];

                return setComparisonProducts(products);
            }
        } catch (error) {
            console.error("Get product by type error:", error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadTableData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const comparisonAttributes = [
        { key: "price", value: "Price" },
        { key: "noise_level", value: "Noise Level" },
        { key: "suitable_for_food_waste", value: "Suitable for food waste" },
        { key: "grind_technology", value: "Grind Technology" },
        { key: "built_in_air_switch", value: "Built-In Air Switch" },
        { key: "grind_chamber_capacity", value: "Grind Chamber Capacity" },
        { key: "plug", value: "Plug" },
        {
            key: "dishwasher_drain_connection",
            value: "Dishwasher Drain Connection",
        },
        { key: "motor", value: "Motor" },
    ];

    const updateScrollButtons = () => {
        if (scrollContainerRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } =
                scrollContainerRef.current;
            const needsScrolling = scrollWidth > clientWidth;
            setShowScrollButtons(needsScrolling);
            setCanScrollLeft(scrollLeft > 0);
            setCanScrollRight(scrollLeft < scrollWidth - clientWidth);
        }
    };

    useEffect(() => {
        // Add a small delay to ensure the table is fully rendered
        const timer = setTimeout(() => {
            updateScrollButtons();
        }, 100);

        const scrollContainer = scrollContainerRef.current;
        if (scrollContainer) {
            scrollContainer.addEventListener("scroll", updateScrollButtons);
            return () => {
                clearTimeout(timer);
                scrollContainer.removeEventListener(
                    "scroll",
                    updateScrollButtons
                );
            };
        }
    }, [comparisonProducts]);

    // Also add a resize observer to handle window resizing
    useEffect(() => {
        const handleResize = () => {
            setTimeout(updateScrollButtons, 100);
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const scrollLeft = () => {
        if (scrollContainerRef.current && canScrollLeft) {
            scrollContainerRef.current.scrollBy({
                left: -250,
                behavior: "smooth",
            });
        }
    };

    const scrollRight = () => {
        if (scrollContainerRef.current && canScrollRight) {
            scrollContainerRef.current.scrollBy({
                left: 250,
                behavior: "smooth",
            });
        }
    };

    return isLoading ? null : comparisonProducts ? (
        <div
            data-testid="product-accordion"
            className="flex w-full justify-center bg-white py-8 lg:py-20"
        >
            <div className="flex w-full flex-col gap-y-9 lg:w-full lg:px-0">
                <h2 className="text-center text-[30px] font-extrabold leading-8 text-brand-black lg:text-[40px]">
                    Comparison Table
                </h2>

                <div
                    className="overflow-x-auto scrollbar-hidden"
                    ref={scrollContainerRef}
                >
                    <table className="comparison-table w-full min-w-[1200px] table-fixed border-collapse rounded-lg !bg-white">
                        <thead className="!bg-white">
                            <tr className="h-16 !bg-white">
                                <th className="min-w-[200px] !bg-white p-4 text-left font-semibold"></th>
                                {comparisonProducts
                                    ?.slice(0, 5)
                                    .map((product, index) => {
                                        const productName =
                                            product.attributes?.find(
                                                (attr) =>
                                                    attr.attribute.code ===
                                                    "product_name"
                                            )?.value || "Unknown Product";
                                        const altText =
                                            typeof productName === "string"
                                                ? productName
                                                : "unknown product";

                                        return (
                                            <th
                                                key={product.id || index}
                                                className="h-16 min-w-[200px] !bg-white p-4"
                                            >
                                                <div className="flex h-full w-full flex-col justify-between gap-4 text-left">
                                                    <div className="flex flex-col items-center justify-center gap-4">
                                                        <img
                                                            src={
                                                                product?.image
                                                                    ?.location ||
                                                                "/images/placeholder.png"
                                                            }
                                                            alt={altText}
                                                            className="max-w-[200px] rounded-[10px] object-cover aspect-square"
                                                        />
                                                        <span className="text-base font-bold">
                                                            {productName}
                                                        </span>
                                                    </div>
                                                    <a
                                                        href={`/products/${product.slug}`}
                                                    >
                                                        <button className="relative inline-flex items-center justify-center whitespace-nowrap rounded-[100px] text-base font-bold leading-snug border !border-brand-black transition-colors active:ring-4 disabled:opacity-25 disabled:cursor-not-allowed bg-transparent text-brand-black  hover:!text-brand-white hover:!bg-brand-black active:text-brand-white active:bg-brand-black active:ring-brand-black/40 h-[50px] px-[60px] my-2 w-full">
                                                            View Product
                                                        </button>
                                                    </a>
                                                </div>
                                            </th>
                                        );
                                    })}
                            </tr>
                        </thead>
                        <tbody>
                            {comparisonAttributes.map((attribute, rowIndex) => (
                                <tr
                                    key={attribute.key}
                                    className="h-12 !bg-white"
                                >
                                    <td
                                        className={`min-w-[200px] font-medium ${
                                            rowIndex % 2 === 0
                                                ? "!bg-brand-light-grey"
                                                : "bg-white"
                                        }`}
                                    >
                                        {attribute.value}
                                    </td>
                                    {comparisonProducts
                                        ?.slice(0, 5)
                                        .map((product, colIndex) => {
                                            const attributeValue =
                                                attribute.key === "price"
                                                    ? `£${(
                                                          product.prices?.price
                                                              ?.incTax / 100
                                                      ).toFixed(2)}`
                                                    : product.keyFeatures?.find(
                                                          (attr) =>
                                                              attr.code ===
                                                              attribute.key
                                                      )?.value || "N/A";

                                            const productTierColour =
                                                product?.attributes?.find(
                                                    (attr) =>
                                                        attr.attribute.code ===
                                                        "product_tier_colour"
                                                )?.value;

                                            return (
                                                <td
                                                    key={`${attribute.key}-${
                                                        product.id || colIndex
                                                    }`}
                                                    className={`min-w-[200px]`}
                                                    style={{
                                                        backgroundColor: `${productTierColour}${
                                                            rowIndex % 2 === 0
                                                                ? "75"
                                                                : "50"
                                                        }`,
                                                    }}
                                                >
                                                    <div className="w-full text-center text-sm font-medium text-black">
                                                        {(() => {
                                                            switch (
                                                                attributeValue
                                                            ) {
                                                                case "Yes":
                                                                    return (
                                                                        <FontAwesomeIcon
                                                                            icon={
                                                                                faCircleCheck
                                                                            }
                                                                            size="lg"
                                                                        />
                                                                    );
                                                                case "No":
                                                                    return (
                                                                        <FontAwesomeIcon
                                                                            icon={
                                                                                faCircleX
                                                                            }
                                                                            size="lg"
                                                                        />
                                                                    );
                                                                default:
                                                                    return attributeValue;
                                                            }
                                                        })()}
                                                    </div>
                                                </td>
                                            );
                                        })}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Scroll buttons - only show if scrolling is needed */}
                {showScrollButtons && (
                    <div className="flex justify-center gap-2">
                        <button
                            onClick={scrollLeft}
                            disabled={!canScrollLeft}
                            className={cn(
                                "flex h-[50px] w-[50px] items-center justify-center rounded-full border-none bg-white shadow-md transition-all duration-200",
                                !canScrollLeft
                                    ? "cursor-not-allowed opacity-50"
                                    : "hover:bg-gray-50 hover:shadow-lg"
                            )}
                        >
                            <FontAwesomeIcon
                                icon={faChevronLeft}
                                className={cn(
                                    "text-lg",
                                    !canScrollLeft
                                        ? "text-gray-400"
                                        : "text-black"
                                )}
                            />
                        </button>
                        <button
                            onClick={scrollRight}
                            disabled={!canScrollRight}
                            className={cn(
                                "flex h-[50px] w-[50px] items-center justify-center rounded-full border-none bg-white shadow-md transition-all duration-200",
                                !canScrollRight
                                    ? "cursor-not-allowed opacity-50"
                                    : "hover:bg-gray-50 hover:shadow-lg"
                            )}
                        >
                            <FontAwesomeIcon
                                icon={faChevronRight}
                                className={cn(
                                    "text-lg",
                                    !canScrollRight
                                        ? "text-gray-400"
                                        : "text-black"
                                )}
                            />
                        </button>
                    </div>
                )}
            </div>
        </div>
    ) : null;
}

export default ProductComparisonTable;
